import {
  forwardRef,
  useCallback,
  useMemo,
} from 'react';
import type {
  Ref,
  ReactElement,
} from 'react';

import styled from 'styled-components';

import type {
  GetOptionLabel,
  GetOptionValue,
  GroupBase,
  SelectComponentsConfig,
} from 'react-select';

import { ModalSelectButton } from '../ModalSelectButton';

import type {
  OptionTypeBase,
  SelectBaseProps,
} from '../ReactSelectExtended/types';

const StyledWrapper = styled.div({
  position: 'relative',
});

export const StyledButton = styled(ModalSelectButton)({
  position: 'absolute',
  top: 0,
  right: 0,
  height: '100%',
  width: 25,
  textAlign: 'left',
}) as typeof ModalSelectButton;

function SelectBaseComponent<
OptionType extends OptionTypeBase,
Group extends GroupBase<OptionType>,
IsMulti extends boolean,
>({
  labelKey = 'label',
  valueKey = 'value',

  defaultComponents,

  renderModal = undefined,
  isDisabled = false,

  onChange = undefined,

  children,

  getOptionLabel: getOptionLabelProp = undefined,
  getOptionValue: getOptionValueProp = undefined,
  components: componentsProp = undefined,
}: SelectBaseProps<OptionType, Group, IsMulti>, ref: Ref<HTMLDivElement>): ReactElement {
  const getOptionLabel = useCallback<GetOptionLabel<OptionType>>((option) => {
    if (getOptionLabelProp) {
      return getOptionLabelProp(option);
    }

    return option[labelKey] as string;
  }, [getOptionLabelProp, labelKey]);

  const getOptionValue = useCallback<GetOptionValue<OptionType>>((option) => {
    if (getOptionValueProp) {
      return getOptionValueProp(option);
    }

    return option[valueKey] as string;
  }, [getOptionValueProp, valueKey]);

  const components = useMemo<SelectComponentsConfig<OptionType, IsMulti, Group>>(() => {
    if (!componentsProp) {
      return defaultComponents;
    }

    return {
      ...defaultComponents,
      ...componentsProp,
    };
  }, [componentsProp, defaultComponents]);

  return (
    <StyledWrapper
      ref={ref}
    >
      {children({
        getOptionLabel,
        getOptionValue,
        components,
      })}

      {
        renderModal && (
          <StyledButton
            renderModal={renderModal}
            disabled={isDisabled}
            onSelectFromModal={onChange}
          />
        )
      }
    </StyledWrapper>
  );
}

const SelectBaseWithRef = forwardRef(SelectBaseComponent);

export const SelectBase = SelectBaseWithRef as <
OptionType extends OptionTypeBase,
Group extends GroupBase<OptionType>,
IsMulti extends boolean,
>(props: SelectBaseProps<OptionType, Group, IsMulti> & {
  ref?: Ref<HTMLDivElement>;
}) => ReactElement;
